/**
 * Session replay suppression.
 *
 * Screens that show patient data from pre-screening hold a suppression while
 * they are mounted. While any hold is active the rrweb recorder stays stopped
 * and drops what it had buffered, and it starts again once the last hold is
 * released. On a practice computer it does not start at all.
 */
import { useLayoutEffect } from 'react';

type SuppressionListener = (suppressed: boolean) => void;

let holds = 0;
const listeners = new Set<SuppressionListener>();

function notify(suppressed: boolean): void {
  for (const listener of [...listeners]) {
    try {
      listener(suppressed);
    } catch (err) {
      console.warn('[replay] suppression listener failed', err);
    }
  }
}

export function isReplaySuppressed(): boolean {
  return holds > 0;
}

/**
 * Stops session replay until the returned function is called. Holds are
 * counted, so the recorder resumes only after every holder released, and
 * releasing twice counts once.
 */
export function suppressReplay(): () => void {
  holds += 1;
  if (holds === 1) notify(true);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds -= 1;
    if (holds === 0) notify(false);
  };
}

/** Listeners run synchronously, so the recorder detaches before the next DOM mutation is observed. */
export function subscribeReplaySuppression(listener: SuppressionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Holds a suppression while the component is mounted and `active` is true.
 * A layout effect runs before rrweb's mutation observer reports the new DOM.
 */
export function useReplaySuppression(active = true): void {
  useLayoutEffect(() => (active ? suppressReplay() : undefined), [active]);
}

/**
 * Whether this desktop app is connected as a practice computer. A bridge that
 * fails to answer counts as connected, so the recorder stays off.
 */
export async function isPracticeComputer(): Promise<boolean> {
  const practice = typeof window === 'undefined' ? undefined : window.rudiDesktop?.practice;
  if (!practice) return false;
  try {
    return (await practice.status()).enrolled === true;
  } catch {
    return true;
  }
}

/** Calls `onEnrolled` when the desktop app reports that it became a practice computer. */
export function watchPracticeEnrollment(onEnrolled: () => void): () => void {
  const practice = typeof window === 'undefined' ? undefined : window.rudiDesktop?.practice;
  if (!practice?.onEvent) return () => undefined;
  try {
    return practice.onEvent(event => {
      if (event.type === 'status' && event.status.enrolled) onEnrolled();
    });
  } catch {
    return () => undefined;
  }
}
